import { QueryClient } from "@tanstack/react-query";

import { getLocalStorageItem, localStorageIsSupported } from "../helpers";
import { queryKey, querySetter } from "../query";
import { APIParams, Prefixes } from "../query/types";
import { announcements } from "./announcements";
import { auth } from "./auth";
import { boards } from "./boards";
import { calendars } from "./calendars";
import { challenges } from "./challenges";
import { connections } from "./connections";
import { discussions } from "./discussions";
import { groups } from "./groups";
import { ideas } from "./ideas";
import { innointel } from "./innointel";
import { invitations } from "./invitations";
import { journey } from "./journey";
import { messages } from "./messages";
import { notifications } from "./notifications";
import { organisations } from "./organisations";
import { pages } from "./pages";
import { persistentTokens } from "./persistentTokens";
import { polls } from "./polls";
import { roles } from "./roles";
import { search } from "./search";
import { superadmin } from "./superadmin";
import { tags } from "./tags";
import { uploads } from "./uploads";
import { users } from "./users";
import { verifications } from "./verifications";
import { webhooks } from "./webhooks";

type APIOptions = {
  getApiUrl: () => string;
  onLogin: (success: () => void, fail: (error: APIError) => void) => void;
  onLogout?: () => void;
  onBeforeRequest?: (xhr: XMLHttpRequest) => void;
  onFailedRequest?: (
    xhr: XMLHttpRequest,
    onFail: (error: APIError) => void,
  ) => void;
};

export type APIError = { status: number; message: string; errorCode?: string };

export class API {
  token: string | null;
  revertToken: string | null;
  webVersion: string | null;
  queryClient: QueryClient;
  options: APIOptions;

  auth: ReturnType<typeof auth>;
  search: ReturnType<typeof search>;
  superadmin: ReturnType<typeof superadmin>;
  challenges: ReturnType<typeof challenges>;
  ideas: ReturnType<typeof ideas>;
  groups: ReturnType<typeof groups>;
  uploads: ReturnType<typeof uploads>;
  discussions: ReturnType<typeof discussions>;
  calendars: ReturnType<typeof calendars>;
  users: ReturnType<typeof users>;
  organisations: ReturnType<typeof organisations>;
  messages: ReturnType<typeof messages>;
  notifications: ReturnType<typeof notifications>;
  polls: ReturnType<typeof polls>;
  tags: ReturnType<typeof tags>;
  webhooks: ReturnType<typeof webhooks>;
  announcements: ReturnType<typeof announcements>;
  invitations: ReturnType<typeof invitations>;
  verifications: ReturnType<typeof verifications>;
  boards: ReturnType<typeof boards>;
  innointel: ReturnType<typeof innointel>;
  journey: ReturnType<typeof journey>;
  roles: ReturnType<typeof roles>;
  persistentTokens: ReturnType<typeof persistentTokens>;
  connections: ReturnType<typeof connections>;
  pages: ReturnType<typeof pages>;

  constructor(options: APIOptions) {
    this.token = null;
    this.revertToken = null;
    this.webVersion = null;
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: 10 * 60 * 1000, // 10 minutes
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      },
    });
    this.options = options;

    this.journey = this.registerRoutes("journey", journey);
    this.auth = this.registerRoutes("auth", auth);
    this.search = this.registerRoutes("search", search);
    this.superadmin = this.registerRoutes("superadmin", superadmin);
    this.challenges = this.registerRoutes("challenges", challenges);
    this.discussions = this.registerRoutes("discussions", discussions);
    this.calendars = this.registerRoutes("calendars", calendars);
    this.ideas = this.registerRoutes("ideas", ideas);
    this.groups = this.registerRoutes("groups", groups);
    this.uploads = this.registerRoutes("uploads", uploads);
    this.users = this.registerRoutes("users", users);
    this.organisations = this.registerRoutes("organisations", organisations);
    this.messages = this.registerRoutes("messages", messages);
    this.notifications = this.registerRoutes("notifications", notifications);
    this.polls = this.registerRoutes("polls", polls);
    this.tags = this.registerRoutes("tags", tags);
    this.webhooks = this.registerRoutes("webhooks", webhooks);
    this.announcements = this.registerRoutes("announcements", announcements);
    this.invitations = this.registerRoutes("invitations", invitations);
    this.verifications = this.registerRoutes("verifications", verifications);
    this.boards = this.registerRoutes("boards", boards);
    this.innointel = this.registerRoutes("innointel", innointel);
    this.roles = this.registerRoutes("roles", roles);
    this.persistentTokens = this.registerRoutes(
      "persistentTokens",
      persistentTokens,
    );
    this.connections = this.registerRoutes("connections", connections);
    this.pages = this.registerRoutes("pages", pages);
  }

  TOKEN_KEY = "auth_token";
  REVERT_TOKEN = "revert_token";

  registerRoutes<
    X extends Record<
      string,
      ((...args: any[]) => any) & { queryName?: string; queryRoute?: string }
    >,
    T extends (api: API) => X,
  >(name: string, route: T) {
    const routes = route(this);
    for (const routeFn of Object.values(routes)) {
      routeFn.queryName = routeFn.name;
      routeFn.queryRoute = name;
    }
    return routes;
  }

  onFailedRequest(xhr: XMLHttpRequest, onFail: (error: APIError) => void) {
    if (this.options.onFailedRequest) {
      this.options.onFailedRequest(xhr, onFail);
      return;
    }
    if (xhr.status === 530) {
      onFail?.({
        status: xhr.status,
        message: "Access from this IP address is not allowed",
      });
      return;
    }
    try {
      const response = JSON.parse(xhr.responseText);
      onFail?.({
        ...response,
        status: xhr.status,
        message: response.message || xhr.statusText,
      });
    } catch {
      onFail?.({ status: xhr.status, message: xhr.statusText });
    }
  }

  req(
    method: string,
    path: string,
    data: any,
    success: (data: any) => void,
    fail: (error: APIError) => void,
    withAuth: boolean,
  ) {
    const xhr = new XMLHttpRequest();

    const url = this.options.getApiUrl();

    xhr.open(method, `${url}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/json");
    if (localStorageIsSupported()) {
      const referrer = getLocalStorageItem("SimplyDo-Referrer");
      if (referrer) {
        xhr.setRequestHeader("SimplyDo-Referrer", referrer);
      }
    }
    if (withAuth && this.token) {
      xhr.setRequestHeader("Authorization", `Bearer ${this.token}`);
    }
    xhr.setRequestHeader("SimplyDo-Device", navigator.userAgent);

    // IANA timezone (e.g. "Europe/London") sourced from the client so the
    // API can render dates in the user's local time without an IP lookup.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        xhr.setRequestHeader("SimplyDo-Timezone", tz);
      }
    } catch {
      // Ignore: not all runtimes expose Intl.DateTimeFormat
    }

    this.options?.onBeforeRequest?.(xhr);

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return;
      }
      if (xhr.status !== 200) {
        this.onFailedRequest?.(xhr, fail);
        return;
      }

      this.webVersion = xhr.getResponseHeader("Simplydo-Webversion");

      try {
        const response = JSON.parse(xhr.responseText);
        success?.(response);
      } catch (err) {
        console.error(err);

        fail?.({
          status: 400,
          message: "There was a problem with this request",
        });
      }
    };
    xhr.send(data && JSON.stringify(data));
  }

  unauthenticatedRequest(
    method: string,
    path: string,
    data: any,
    success: (data: any) => void,
    fail: (error: APIError) => void,
  ) {
    this.req(method, path, data, success, fail, false);
  }

  authenticatedRequest(
    method: string,
    path: string,
    data: any,
    success: (data: any) => void,
    fail: (error: APIError) => void,
  ) {
    if (!this.token) {
      fail?.({ status: 401, message: "Not authenticated" });
      return;
    }
    this.req(method, path, data, success, fail, true);
  }

  maybeAuthenticatedRequest(
    method: string,
    path: string,
    data: any,
    success: (data: any) => void,
    fail: (error: APIError) => void,
  ) {
    if (this.token) {
      this.authenticatedRequest(method, path, data, success, fail);
    } else {
      this.unauthenticatedRequest(method, path, data, success, fail);
    }
  }

  setQueryData<
    T extends (...args: any[]) => any,
    APIParam extends APIParams<T>,
    TBody extends Parameters<APIParam[1]>[0],
    Undef extends boolean = false,
    Cached extends boolean = false,
  >(
    fn: T,
    params: APIParam[0] extends { 0: any } ? Prefixes<APIParam[0]> | [] : [],
    dataOrCallback:
      | undefined
      | TBody
      | (Undef extends true ?
          (data: TBody | undefined, variables: APIParam[0]) => TBody | undefined
        : (data: TBody, variables: APIParam[0]) => TBody),
    options?: {
      exact?: boolean;
      allowUndefined?: Undef;
      returnCached?: Cached;
    },
  ): Cached extends true ? TBody | undefined : undefined {
    const cached =
      options?.returnCached ?
        this.queryClient.getQueryData(queryKey(fn, params))
      : undefined;

    this.queryClient.setQueriesData(
      { queryKey: queryKey(fn, params), exact: options?.exact ?? false },
      querySetter(fn, dataOrCallback, {
        allowUndefined: options?.allowUndefined ?? false,
      }),
    );

    return cached as Cached extends true ? TBody | undefined : undefined;
  }

  invalidateQueries<
    T extends (...args: any[]) => any,
    APIParam extends APIParams<T>,
  >(
    fn?: T,
    ...params:
      | (APIParam[0] extends { 0: any } ?
          [APIParam[0]] | [Prefixes<APIParam[0]>]
        : [])
      | []
      | [[]]
  ) {
    if (!fn) {
      this.queryClient.invalidateQueries();
      return;
    }

    this.queryClient.invalidateQueries({ queryKey: queryKey(fn, ...params) });
  }
}
